import { motion } from 'framer-motion';
import { useEffect } from 'react';
import LanguageToggle from '../components/ui/LanguageToggle';

// --- Barcode SVG ---
function Barcode({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 180 40" className={className} fill="none" aria-hidden="true">
      <rect x="0" y="0" width="180" height="40" fill="white" />
      <g stroke="black" strokeWidth="3">
        <line x1="10" y1="5" x2="10" y2="35" />
        <line x1="18" y1="5" x2="18" y2="35" />
        <line x1="26" y1="5" x2="26" y2="35" />
        <line x1="34" y1="5" x2="34" y2="35" />
        <line x1="44" y1="5" x2="44" y2="35" />
        <line x1="52" y1="5" x2="52" y2="35" />
        <line x1="60" y1="5" x2="60" y2="35" />
        <line x1="70" y1="5" x2="70" y2="35" />
        <line x1="80" y1="5" x2="80" y2="35" />
        <line x1="90" y1="5" x2="90" y2="35" />
        <line x1="100" y1="5" x2="100" y2="35" />
        <line x1="110" y1="5" x2="110" y2="35" />
        <line x1="120" y1="5" x2="120" y2="35" />
        <line x1="130" y1="5" x2="130" y2="35" />
        <line x1="140" y1="5" x2="140" y2="35" />
        <line x1="150" y1="5" x2="150" y2="35" />
        <line x1="160" y1="5" x2="160" y2="35" />
        <line x1="170" y1="5" x2="170" y2="35" />
      </g>
    </svg>
  );
}

// --- Large Stamp ---
function Stamp({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <motion.div
      whileHover={{ rotate: [0, -4, 4, 0], scale: 1.04 }}
      className={`inline-block px-6 py-2 border-4 border-black bg-white shadow-xl font-black text-3xl md:text-6xl uppercase tracking-tighter select-none ${className}`}
      style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif', letterSpacing: '-0.06em' }}
    >
      {children}
    </motion.div>
  );
}

export default function AboutUs() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen w-full bg-white text-black font-sans overflow-x-hidden">
      {/* Language Toggle Top Right */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageToggle />
      </div>

      {/* HERO BLOCK */}
      <section className="relative w-full min-h-[60vh] flex flex-col justify-center items-center px-4 py-12 border-b-4 border-black overflow-hidden">
        {/* Barcode top left */}
        <motion.div initial={{ x: -40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.2 }} className="absolute top-4 left-2 z-10">
          <Barcode className="w-32 h-8" />
        </motion.div>
        
        {/* Navigation back to home */}
        <div className="absolute top-4 right-4 z-20">
          <a 
            href="/"
            className="bg-black text-white px-4 py-2 border-2 border-black hover:bg-white hover:text-black transition-all duration-300 font-bold uppercase tracking-wider text-sm"
            style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
          >
            ← Back Home
          </a>
        </div>

        {/* Giant Logo Stamp */}
        <div className="flex flex-col items-center gap-2 z-20">
          <Stamp className="mb-2 rotate-[-3deg]">ABOUT US</Stamp>
          <div className="flex flex-row items-center gap-2">
            <span className="text-xs md:text-lg font-bold tracking-widest uppercase bg-black text-white px-2 py-1 rotate-2">Our Story</span>
            <span className="text-xs md:text-lg font-bold tracking-widest uppercase bg-black text-white px-2 py-1 -rotate-2">Our Mission</span>
          </div>
        </div>

        {/* Tagline */}
        <div className="mt-4 text-center text-base md:text-xl font-semibold text-black/80 max-w-lg mx-auto z-20" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
          Discover the passion, creativity, and love behind Zoomies & Co
        </div>

        {/* Overlapping barcode bottom right */}
        <motion.div initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }} className="absolute bottom-4 right-2 z-10">
          <Barcode className="w-32 h-8" />
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <section className="w-full py-16 px-4 border-b-4 border-black bg-white flex flex-col items-center">
        <div className="max-w-4xl w-full">
          {/* Our Story */}
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-8 text-center" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              Our Story
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-64 h-64 md:w-80 md:h-80 border-4 border-black bg-white rounded-full overflow-hidden shadow-xl">
                    <img src="/pop.jpeg" alt="Zoomies & Co mascot" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-black text-white flex items-center justify-center rounded-full border-4 border-white shadow-lg">
                    <span className="text-2xl">🐾</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                <div className="bg-black text-white p-6 border-4 border-black shadow-xl">
                  <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                    Born for the Pets Who Run the Show
                  </h3>
                  <p className="text-lg leading-relaxed">
                    Zoomies & Co was born for the pets who run the show — and the humans who happily let them. From tail-wagging tools to purr-worthy wellness, everything we curate is handpicked to feel like it came from your inner circle — not a random aisle in a mega-store.
                  </p>
                </div>
                
                <div className="bg-white border-4 border-black p-4 shadow-lg">
                  <p className="text-center font-bold text-lg">
                    "Where every pet's story begins with <span className="text-pink-500">LOVE</span>"
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Our Mission */}
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-8 text-center" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              Our Mission
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-8 border-4 border-black bg-gradient-to-br from-pink-100 to-purple-200 shadow-xl transform hover:scale-105 transition-transform duration-300">
                <div className="text-6xl mb-6">🐾</div>
                <h3 className="text-2xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                  The Right Essentials
                </h3>
                <p className="text-lg leading-relaxed font-bold">
                  The right essentials should feel like you never left their side.
                </p>
              </div>
              <div className="text-center p-8 border-4 border-black bg-gradient-to-br from-blue-100 to-cyan-200 shadow-xl transform hover:scale-105 transition-transform duration-300">
                <div className="text-6xl mb-6">💞</div>
                <h3 className="text-2xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                  Every Item Whispers
                </h3>
                <p className="text-lg leading-relaxed font-bold">
                  Every item should whisper, "I see you, I love you." Like a Zoomies moment in slow motion — gentle, joyful, and full of heart.
                </p>
              </div>
              <div className="text-center p-8 border-4 border-black bg-gradient-to-br from-green-100 to-emerald-200 shadow-xl transform hover:scale-105 transition-transform duration-300">
                <div className="text-6xl mb-6">💌</div>
                <h3 className="text-2xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                  Your Pet's Joy
                </h3>
                <p className="text-lg leading-relaxed font-bold">
                  And your pet's joy? It's the most important package we'll ever ship.
                </p>
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="mb-16">
            <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-8 text-center" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
              Our Values
            </h2>
            <div className="bg-black text-white p-8 border-4 border-black shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                    🫶 Kind to All Creatures
                  </h3>
                  <p className="text-lg leading-relaxed">
                    We work with partners who value quality, compassion, and care — choosing items that are gentle on paws, safe for homes, and mindful of our shared world.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                    💞 Built on Love & Zoomies
                  </h3>
                  <p className="text-lg leading-relaxed">
                    Everything we do is for that one tail wag, one happy bounce, one deep breath of "ahhh." That's our why — and always will be.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white border-4 border-black p-8 shadow-xl">
              <h3 className="text-3xl font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}>
                Join Our Journey
              </h3>
              <p className="text-lg mb-6 max-w-2xl mx-auto">
                Be part of our story as we continue to create amazing products for pets and their humans. Sign up for early access and exclusive updates!
              </p>
              <a 
                href="/"
                className="inline-block bg-black text-white px-8 py-4 border-4 border-black hover:bg-white hover:text-black transition-all duration-300 font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                style={{ fontFamily: 'Impact, Helvetica Neue, Arial, sans-serif' }}
              >
                Back to Homepage
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="w-full py-6 px-4 grid grid-cols-1 md:grid-cols-3 items-center bg-white border-t-4 border-black gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-4 justify-center md:justify-start">
          <a href="https://www.instagram.com/zoomiesandco.official/" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform hover:opacity-70" aria-label="Instagram">
            <svg width="24" height="24" fill="none" stroke="black" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17" cy="7" r="1.5"/></svg>
          </a>
        </div>
        <div className="flex flex-col items-center justify-center text-center font-bold text-lg md:text-xl tracking-widest mx-2">
          <div className="flex items-center justify-center gap-2">
            <span className="text-2xl md:text-3xl" aria-label="paw" role="img">🐾</span>
            <span className="text-2xl md:text-3xl" aria-label="dog" role="img">🐶</span>
          </div>
          <span className="mt-1">Spreading joy, one wag at a time</span>
        </div>
        <div className="flex gap-4 justify-center md:justify-end">
          <a href="#" className="hover:underline hover:opacity-70">Contact</a>
          <a href="#" className="hover:underline hover:opacity-70">Email</a>
          <a href="#" className="hover:underline hover:opacity-70">Terms</a>
        </div>
      </footer>
    </div>
  );
} 